import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './CategoryBrowseTemplate.module.css'
import Breadcrumbs from 'components/product/Breadcrumbs'
import DealCtaPill from 'components/product/DealCtaPill'
import DealHuntProductCard from 'components/product/DealHuntProductCard'
import { cardTitle, categoryHitToCardModel } from 'components/product/cardModel'
import ActiveTokens from 'components/category/rail/ActiveTokens'
import AllFiltersDrawer from 'components/category/rail/AllFiltersDrawer'
import FilterRail from 'components/category/rail/FilterRail'
import { useCategoryBrowse, type CategoryBrowseData } from 'components/category/useCategoryBrowse'
import OwnCaptureBand, { type GridNumbers } from 'components/owner-notes/OwnCaptureBand'
import { numbersFromHit } from 'lib/owner-notes/compare-source'
import { asksQuestions } from 'lib/owner-notes/category-question'
import { useDeclaredState } from 'lib/owner-notes/declared'
import LabSurfaces from 'components/product/LabSurfaces'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { SORT_OPTIONS, isSortKey } from 'lib/dealhunt/constants'
import { countActiveFilters, setSort, type CategoryFilterState } from 'lib/dealhunt/filters'
import { dealHuntCategoryHref } from 'lib/dealhunt/url'
import { isAudioSlug } from 'lib/dealhunt/client'
import { organicCards, spliceSponsoredHit } from 'lib/dealhunt/sponsored'
import type { DealHuntCategory } from 'lib/dealhunt/types'
import {
  activeFiltersParam,
  CatalogAnalyticsProvider,
  type CatalogAnalyticsScope,
  TOP_PRODUCTS,
  useCatalogEvent,
  useListingAnalytics,
} from 'lib/analytics/catalog'

interface CategoryBrowseTemplateProps {
  data: DealHuntCategory
  initialFilters: CategoryFilterState
  initialBrowse: CategoryBrowseData
}

/**
 * Model-driven category page: header → type tabs → match headline + sort →
 * [desktop rail | grid] → all-filters drawer / mobile sheet. Everything the rail
 * shows comes from the filter model (`/api/category/v3`).
 */
const CategoryBrowseBody: React.VFC<CategoryBrowseTemplateProps> = ({
  data,
  initialFilters,
  initialBrowse,
}) => {
  const {
    filters,
    data: browse,
    isLoading,
    isLoadingMore,
    applyFilters: applyFiltersRaw,
    loadMore,
    ensureFilterModel,
    responseSeq,
  } = useCategoryBrowse(data.node.id, initialFilters, initialBrowse)
  const { track } = useCatalogEvent()
  const questionCategory = useMemo(() => {
    if (!asksQuestions(data.node)) {
      return null
    }
    return { nodeId: data.node.id, fullSlug: data.node.fullSlug }
  }, [data.node])
  // Stable per response: `DealHuntProductCard` is memoized on `product` identity. Page 0's
  // sponsored line leads the grid (contract C1) and is deduped out of every listing page;
  // positions stay 1-based over the spliced list.
  const { sponsoredSlot } = browse
  const cards = useMemo(
    () =>
      spliceSponsoredHit(browse.hits, sponsoredSlot).map(hit =>
        categoryHitToCardModel(hit, data.subcategories, {
          showReleaseDate: filters.sortBy === 'release_date',
          bottomLine: true,
          sponsor: hit === sponsoredSlot?.hit ? sponsoredSlot.sponsor : null,
        })
      ),
    [browse.hits, sponsoredSlot, data.subcategories, filters.sortBy]
  )
  const { applyFilters } = useListingAnalytics({
    pageType: 'category_page',
    filters,
    applyFilters: applyFiltersRaw,
    resultCount: browse.nbHits,
    page: browse.page,
    response: responseSeq,
    // The organic top: the sponsored card never counts as the listing's top product.
    topProducts: organicCards(cards).slice(0, TOP_PRODUCTS).map(cardTitle),
  })
  // Card clicks carry the filter set they happened under — same values as `list_view`.
  const listContext = useMemo(
    () => ({ active_filters: activeFiltersParam(filters, true), sort_by: filters.sortBy }),
    [filters]
  )
  // What the grid knows per product (node id → numbers + slug) — the capture band uses it
  // to enrich the reader's declared product with axes the typeahead doesn't carry.
  const gridNumbers = useMemo(() => {
    const map = new Map<string, GridNumbers>()
    browse.hits.forEach(hit => {
      map.set(hit.objectID, {
        slug: hit.fullSlug,
        name: hit.displayName ?? hit.canonicalName ?? hit.title,
        imageUrl: hit.cloudinaryImageUrl || hit.imageUrl,
        ...numbersFromHit(hit),
        cardOwners: hit.owners ?? null,
      })
    })
    return map
  }, [browse.hits])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const openDrawer = (): void => {
    ensureFilterModel()
    setDrawerOpen(true)
  }
  const trailingSlash = publicConfig('useTrailingSlash')
  const tabsRef = useRef<HTMLElement | null>(null)

  // Mobile: the type strip overflows, so open with the current category centred
  // (then the reader slides freely). Computed scrollLeft on the strip only — never
  // scrollIntoView, which would also scroll the page. No-op when nothing overflows.
  useEffect(() => {
    const strip = tabsRef.current
    if (!strip) {
      return
    }
    const current = strip.querySelector<HTMLElement>('[aria-current="page"]')
    if (!current || strip.scrollWidth <= strip.clientWidth) {
      return
    }
    strip.scrollLeft = current.offsetLeft - (strip.clientWidth - current.offsetWidth) / 2
  }, [data.node.fullSlug])

  const { summary, filters: model } = browse
  const hasMore = browse.page + 1 < browse.nbPages
  const active = countActiveFilters(filters)

  const subtitle = `Compare prices, specs, and our reviews across ${summary.total.toLocaleString(
    'en-US'
  )} products — ${summary.tested.toLocaleString('en-US')} tested by our team.`
  const matchSub =
    active > 0
      ? `of ${summary.total.toLocaleString('en-US')} catalogued${
          summary.matchedTested > 0
            ? ` · ${summary.matchedTested.toLocaleString('en-US')} tested by us`
            : ''
        }`
      : `Everything we have catalogued · ${summary.tested.toLocaleString('en-US')} tested by us`

  // One "Browse by type" row. A node with children (Headphones → Over-Ear, Gaming…)
  // shows them, "All" current; a leaf shows its siblings (the same list, itself
  // current) — the legacy "Browse by category" row, not the parent-level peer strip
  // the first v3 cut drew here (redundant with the header nav on parent pages).
  const tabs: { id: string; name: string; fullSlug: string; count: number; current: boolean }[] =
    data.subcategories.length > 0
      ? [
          {
            id: 'all',
            name: 'All',
            fullSlug: data.node.fullSlug,
            count: summary.total,
            current: true,
          },
          ...data.subcategories.map(s => ({
            id: s.id,
            name: s.name,
            fullSlug: s.fullSlug,
            count: s.productCount,
            current: false,
          })),
        ]
      : browse.siblings
          // Audio subtree only: a childless node's siblings could be the other Electronics
          // departments (the audio root's peers are Phones, Laptops…) — never list those.
          .filter(
            (s): s is typeof s & { fullSlug: string } =>
              s.fullSlug !== null && isAudioSlug(s.fullSlug)
          )
          .map(s => ({ ...s, current: s.fullSlug === data.node.fullSlug }))

  return (
    <div className={styles.page}>
      <div className={styles.crumbRow}>
        <Breadcrumbs items={data.breadcrumbs} />
        <DealCtaPill />
      </div>
      <header className={styles.hero}>
        <h1 className={styles.title}>{data.node.name}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
      </header>

      {tabs.length > 0 && (
        <nav ref={tabsRef} className={styles.tabs} aria-label="Browse by type">
          <span className={styles.tabsLabel}>{'Browse by type'}</span>
          {tabs.map(tab => {
            if (tab.current) {
              return (
                <span
                  key={tab.id}
                  className={clsx(styles.tab, styles.tabActive)}
                  aria-current="page"
                >
                  {tab.name}
                </span>
              )
            }
            return (
              <Linked
                key={tab.id}
                href={dealHuntCategoryHref(tab.fullSlug, trailingSlash)}
                className={styles.tab}
                onClick={() =>
                  track('category_page_type_click', {
                    target_title: tab.name,
                    target_id: tab.fullSlug,
                    item_count: tab.count,
                  })
                }
              >
                {tab.name}
                <span className={styles.tabCount}>{tab.count.toLocaleString('en-US')}</span>
              </Linked>
            )
          })}
        </nav>
      )}

      <div className={styles.toolbar}>
        <div className={styles.matchHead}>
          <span className={styles.matchCount}>
            {`${summary.matched.toLocaleString('en-US')} ${
              summary.matched === 1 ? 'product matches' : 'products match'
            }`}
          </span>
          <span className={styles.matchSub}>{matchSub}</span>
        </div>
        <div className={styles.toolbarRight}>
          <button
            type="button"
            className={styles.filterToggle}
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={openDrawer}
            onPointerEnter={ensureFilterModel}
            onFocus={ensureFilterModel}
          >
            {'Filters'}
            {active > 0 && <span className={styles.filterBadge}>{active}</span>}
          </button>
          <label className={styles.sort}>
            <span className={styles.sortLabel}>{'Sort'}</span>
            <select
              className={styles.sortSelect}
              value={filters.sortBy}
              onChange={e => {
                if (isSortKey(e.target.value)) {
                  applyFilters(setSort(filters, e.target.value))
                }
              }}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <ActiveTokens tokens={model.applied} filters={filters} onChange={applyFilters} />

      {/* Owner capture (owner-notes contract §1/#63): the "Own a pair already?" ask, full-width
          between the tokens and the rail|grid layout. Server-renders the ask only; the
          declaration and the deltas hydrate from localStorage so the edge-cached HTML stays
          visitor-invariant. Once declared it becomes the reference strip, in the same place.
          Under it, "Ask people who own <noun>" (category questions SG-1) on the nodes DealHunt
          files a question under (`asksQuestions`). */}
      <OwnCaptureBand
        categoryName={data.node.name}
        categoryNoun={summary.noun}
        gridNumbers={gridNumbers}
        questionCategory={questionCategory}
      />

      <div className={styles.layout}>
        <div className={styles.railCol}>
          <FilterRail
            model={model}
            summary={summary}
            filters={filters}
            onChange={applyFilters}
            onOpenAll={openDrawer}
            allFiltersOpen={drawerOpen}
            onPrefetchAll={ensureFilterModel}
          />
        </div>

        <div className={styles.results}>
          {browse.hits.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyTitle}>{'Nothing matches this combination.'}</span>
              {'Loosen a filter — every value shows its count, so you can see where the wall is.'}
            </div>
          ) : (
            <div className={clsx(styles.grid, { [styles.gridLoading]: isLoading })}>
              {cards.map((card, i) => (
                <DealHuntProductCard
                  key={card.key}
                  product={card}
                  ctaToRetailer
                  className={styles.card}
                  list={{ list_name: 'category', position: i + 1, ...listContext }}
                />
              ))}
            </div>
          )}
          {hasMore && (
            <div className={styles.loadMoreRow}>
              <button
                type="button"
                className={styles.loadMore}
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading…' : 'Load more products'}
              </button>
            </div>
          )}
        </div>
      </div>

      <AllFiltersDrawer
        open={drawerOpen}
        categoryNodeId={data.node.id}
        model={model}
        summary={summary}
        filters={filters}
        categoryName={data.node.name}
        moreLoading={browse.moreDeferred === true}
        onChange={applyFilters}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  )
}

const CategoryBrowseTemplate: React.VFC<CategoryBrowseTemplateProps> = props => {
  const { data } = props
  // Common params for every category_page_* event (docs/analytics-catalog-events.md).
  // Memoised: the provider publishes this object as the page scope on identity change.
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'category_page',
      category: data.node.name,
      category_slug: data.node.fullSlug,
      category_id: data.node.id,
    }),
    [data.node]
  )
  // One declared-product state for the page: the capture band writes it, every card
  // reads it (owner-notes contract #63). Read after hydration only.
  const declared = useDeclaredState()
  return (
    <CatalogAnalyticsProvider value={scope}>
      {/* The lab dialog + pinned drawer (2026-09-08) mount on a declared pair only: both
          render nothing without one here (so does every card's pin). */}
      <LabSurfaces declared={declared}>
        <CategoryBrowseBody {...props} />
      </LabSurfaces>
    </CatalogAnalyticsProvider>
  )
}

export default CategoryBrowseTemplate
